import React, { useMemo, useState, useEffect } from "react";

/**
 * Super Homes — Landing Page
 * Stack: React + Tailwind CSS (no external UI libs)
 *
 * Notes for integration:
 * - Replace `SUBSCRIBE_ENDPOINT` with your Django/DRF API route (e.g., "/api/subscribe").
 * - Tailwind required in host app. Suggested container width: max-w-7xl.
 * - Sections included: Header, Hero, Vision, Divisions (4 only), Why Us,
 *   Modular Factory, Roadmap, Subscribe modal, Footer.
 * - Removed: Social Proof, For Partners, Primary Lead Capture. (Per requirements.)
 * - "All Things Interior" division intentionally excluded. (Per requirements.)
 */

const SUBSCRIBE_ENDPOINT = "/api/subscribe"; // <-- set your DRF endpoint here

// Use the logo from public directory
const LOGO_SRC = "/logo.png";

// Simple icons
const IconBolt = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path fill="currentColor" d="M13 3L4 14h6l-1 7 9-11h-6l1-7z"/>
  </svg>
);
const IconCube = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path fill="currentColor" d="M12 2l9 5v10l-9 5-9-5V7l9-5zm0 2.2L6 7l6 3.3L18 7 12 4.2zM6 9.3v5.4L12 18v-5.4L6 9.3zm12 0l-6 3.3V18l6-3.3V9.3z"/>
  </svg>
);
const IconApp = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path fill="currentColor" d="M4 4h6v6H4V4zm0 10h6v6H4v-6zm10-10h6v6h-6V4zm0 10h6v6h-6v-6z"/>
  </svg>
);
const IconCheck = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path fill="currentColor" d="M9 16.17l-3.88-3.88L3.7 13.7 9 19l12-12-1.41-1.41z"/>
  </svg>
);
const IconMenu = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path fill="currentColor" d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"/>
  </svg>
);
const IconClose = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path fill="currentColor" d="M18.3 5.71L12 12.01 5.7 5.7 4.3 7.12 10.6 13.4 4.29 19.7l1.41 1.41L12 14.83l6.3 6.29 1.41-1.41-6.29-6.29 6.29-6.29-1.41-1.41z"/>
  </svg>
);

function classNames(...xs) { return xs.filter(Boolean).join(" "); }

const Header = ({ onOpenSubscribe }) => {
  const [open, setOpen] = useState(false);
  
  // Scroll lock when sidebar is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.touchAction = 'auto';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.touchAction = 'auto';
    };
  }, [open]);

  const NavLink = ({href, children}) => (
    <a href={href} className="text-sm font-medium text-slate-600 hover:text-slate-900 hover:text-superhomes-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-superhomes-400 rounded" onClick={() => setOpen(false)}>
      {children}
    </a>
  );

  return (
    <>
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200/70">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-20 items-center justify-between">
            <a href="#top" className="flex items-center gap-3 py-3" aria-label="Super Homes">
              <img src={LOGO_SRC} alt="Super Homes" className="h-12 w-auto sm:h-14" />
            </a>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-7" aria-label="Primary">
              <NavLink href="#vision">Vision</NavLink>
              <NavLink href="#divisions">Divisions</NavLink>
              <NavLink href="#why">Why Us</NavLink>
              <NavLink href="#factory">Factory</NavLink>
              <NavLink href="#roadmap">Roadmap</NavLink>
              <a href="https://investors.superhomes.app" className="text-sm font-medium text-slate-600 hover:text-superhomes-500">Investors</a>
            </nav>

            {/* CTAs */}
            <div className="hidden md:flex items-center gap-3">
              <a
                href="#subscribe"
                onClick={(e)=> { e.preventDefault(); onOpenSubscribe(); }}
                className="inline-flex items-center rounded-xl bg-superhomes-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-superhomes-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-superhomes-400"
              >
                Subscribe for Updates
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              aria-label="Open menu"
              className="md:hidden inline-flex items-center justify-center rounded-lg p-2 text-slate-700 hover:bg-superhomes-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-superhomes-400"
              onClick={() => setOpen(true)}
            >
              <IconMenu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer - Outside header for proper positioning */}
      {open && (
        <div className="md:hidden fixed inset-0 z-[60]" role="dialog" aria-modal="true">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ease-out" 
            onClick={() => setOpen(false)} 
          />
          <div className="absolute inset-y-0 right-0 w-full max-w-xs bg-white shadow-2xl p-6 flex flex-col gap-4 transform transition-transform duration-300 ease-out translate-x-0">
            <div className="flex items-center justify-between">
              <img src={LOGO_SRC} alt="Super Homes" className="h-10 w-auto" />
              <button aria-label="Close menu" onClick={() => setOpen(false)} className="rounded-lg p-2 hover:bg-superhomes-50">
                <IconClose className="h-6 w-6 text-slate-700" />
              </button>
            </div>
            <nav className="grid gap-3 text-slate-700">
              <a href="#vision" onClick={() => setOpen(false)} className="py-2">Vision</a>
              <a href="#divisions" onClick={() => setOpen(false)} className="py-2">Divisions</a>
              <a href="#why" onClick={() => setOpen(false)} className="py-2">Why Us</a>
              <a href="#factory" onClick={() => setOpen(false)} className="py-2">Factory</a>
              <a href="#roadmap" onClick={() => setOpen(false)} className="py-2">Roadmap</a>
              <a href="https://investors.superhomes.app" className="py-2">Investors</a>
            </nav>
            <div className="mt-2">
              <a
                href="#subscribe"
                onClick={(e)=>{ e.preventDefault(); setOpen(false); onOpenSubscribe(); }}
                className="inline-flex items-center justify-center rounded-xl bg-superhomes-500 px-4 py-2 text-sm font-semibold text-white hover:bg-superhomes-600 w-full"
              >
                Subscribe for Updates
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Pill = ({children}) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-sm">
    {children}
  </span>
);

const Hero = ({ onOpenSubscribe }) => (
  <section id="top" className="relative isolate">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-8 pb-16 sm:pt-16 sm:pb-20">
      <div className="grid items-center gap-10 lg:grid-cols-2">
        {/* Hero Text - Order 2 on mobile, 1 on desktop */}
        <div className="order-2 lg:order-1">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
            Everything Interiors. <span className="text-superhomes-500">One Super Platform.</span>
          </h1>
          <p className="mt-5 text-lg leading-7 text-slate-600 max-w-prose">
            Turnkey interiors, windows, fabrication, and bathrooms—integrated by a modular giga‑factory and a single app.
            Transparent BOQs, warranties that live in your app, and factory‑fast timelines.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#divisions" className="inline-flex items-center rounded-xl bg-superhomes-500 px-5 py-3 text-sm font-semibold text-white hover:bg-superhomes-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-superhomes-400">Explore Divisions</a>
            <button onClick={onOpenSubscribe} className="inline-flex items-center rounded-xl border border-superhomes-200 px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-superhomes-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-superhomes-400">Subscribe for Updates</button>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            <Pill>Marvel Homes · 10‑Year Warranty</Pill>
            <Pill>Just Bathrooms · 15‑Day Renovation</Pill>
            <Pill>Vantage · Lifetime Window Warranty</Pill>
          </div>
        </div>
        {/* Hero Image - Order 1 on mobile, 2 on desktop */}
        <div className="relative order-1 lg:order-2 lg:justify-self-end">
          {/* Hero Image */}
          <div className="w-full max-w-xl rounded-2xl overflow-hidden shadow-lg border border-slate-200 bg-slate-50">
            <img 
              src="/hero-image.png" 
              alt="Super Homes - Modular Giga Factory and Interior Solutions" 
              className="w-full h-auto object-contain"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Vision = () => (
  <section id="vision" className="py-16 sm:py-24 bg-white">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Building India's Largest Interior Super Platform</h2>
      <p className="mt-4 max-w-3xl text-slate-600 text-lg">
        We're unifying India's fragmented interiors market into a single, trusted experience—powered by a modular giga‑factory and a super app.
      </p>
      <div className="mt-10 grid gap-6 sm:grid-cols-3">
        {[
          {icon: IconCube, title: "Integrated Supply Chain", desc: "Design → Factory → Installation → After‑Care, all in one system."},
          {icon: IconBolt, title: "Modular Mega‑Factory", desc: "Lego‑style bays with shared utilities for rapid new vertical launches."},
          {icon: IconApp, title: "One Super App", desc: "Quotes, tracking, warranties, and support under a single account."},
        ].map((i, idx) => (
          <div key={idx} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <i.icon className="h-7 w-7 text-superhomes-500" />
            <h3 className="mt-4 text-lg font-semibold text-slate-900">{i.title}</h3>
            <p className="mt-1 text-sm text-slate-600">{i.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Divisions = () => (
  <section id="divisions" className="py-16 sm:py-24 bg-slate-50">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Our Divisions</h2>
        <p className="mt-4 text-lg text-slate-600 max-w-3xl mx-auto">Live divisions delivering excellence today. Each built on our unified factory platform for consistent quality and seamless integration.</p>
      </div>

      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4">
        {[
          {
            name: "Marvel Homes", 
            tagline: "Turnkey Interiors", 
            description: "Complete home interior solutions from design to installation with 10-year warranty.",
            features: ["Design‑to‑handover", "Transparent BOQs", "10‑year warranty"],
            color: "bg-gradient-to-br from-blue-50 to-indigo-100",
            accentColor: "border-blue-200",
            href: "https://marvelhomes.pro"
          },
          {
            name: "The Metal Store", 
            tagline: "Fabrication & Metal Furniture", 
            description: "Custom metal solutions for gates, furniture, and architectural elements.",
            features: ["Gazebos & sheds", "Gates & grills", "Custom railings"],
            color: "bg-gradient-to-br from-gray-50 to-slate-100",
            accentColor: "border-gray-200",
            href: "https://themetal.store"
          },
          {
            name: "Just Bathrooms", 
            tagline: "15‑Day Renovations", 
            description: "Complete bathroom makeovers with smart layouts and branded hardware.",
            features: ["Smart layouts", "Branded hardware", "Waterproofing guarantee"],
            color: "bg-gradient-to-br from-teal-50 to-cyan-100",
            accentColor: "border-teal-200",
            href: "https://justbathrooms.in/"
          },
          {
            name: "Vantage", 
            tagline: "uPVC & Aluminium Windows", 
            description: "Energy-efficient windows with lifetime warranty and superior noise reduction.",
            features: ["Energy‑efficient", "Noise reduction", "Lifetime warranty"],
            color: "bg-gradient-to-br from-green-50 to-emerald-100",
            accentColor: "border-green-200",
            href: "https://vantagewindows.in/"
          },
        ].map((division, idx) => (
          <article key={idx} className="group relative bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
            {/* Logo placeholder */}
            <div className={`${division.color} px-6 pt-6 pb-4`}>
              <div className={`w-16 h-16 rounded-xl border-2 ${division.accentColor} bg-white/80 flex items-center justify-center mb-4`}>
                <div className="w-8 h-8 bg-superhomes-400 rounded opacity-60"></div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 pb-6">
              <h3 className="text-xl font-bold text-slate-900">{division.name}</h3>
              <p className="text-superhomes-600 font-semibold text-sm mb-3">{division.tagline}</p>
              <p className="text-slate-600 text-sm mb-4 leading-relaxed">{division.description}</p>
              
              {/* Features */}
              <div className="space-y-2 mb-6">
                {division.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <IconCheck className="h-4 w-4 text-superhomes-500 flex-shrink-0"/>
                    <span className="text-sm text-slate-600">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <a 
                href={division.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 w-full justify-center rounded-xl bg-superhomes-500 px-4 py-3 text-sm font-semibold text-white hover:bg-superhomes-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-superhomes-400 focus-visible:ring-offset-2"
              >
                Explore {division.name}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </article>
        ))}
      </div>

      {/* Coming Soon teaser */}
      <div className="mt-12 text-center">
        <p className="text-slate-500 text-sm mb-4">More divisions launching soon...</p>
        <div className="flex justify-center gap-2">
          {["Modular Kitchens", "Home Automation", "Flooring Solutions"].map((upcoming, i) => (
            <span key={i} className="inline-block px-3 py-1 bg-white border border-slate-200 rounded-full text-xs text-slate-600">
              {upcoming}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);


const WhyUs = () => (
  <section id="why" className="py-16 sm:py-24 bg-white">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Why Choose Super Homes?</h2>
        <p className="mt-4 text-lg text-slate-600 max-w-3xl mx-auto">
          We're revolutionizing India's interior industry with factory-first approach, transparent processes, and unified platform that delivers consistency at scale.
        </p>
      </div>

      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {[
          {
            icon: (
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            ),
            title: "Factory‑First Quality", 
            description: "Precision manufacturing with tighter tolerances ensures perfect fit and faster installations every time.",
            highlight: "50% faster installation"
          },
          {
            icon: (
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
            title: "Time‑Bound Execution", 
            description: "Guaranteed delivery timelines with SLAs like 15‑day bathroom renovations backed by our factory efficiency.",
            highlight: "15-day bathrooms"
          },
          {
            icon: (
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            ),
            title: "Transparent Pricing", 
            description: "Itemized BOQs with detailed breakdowns. No hidden costs, no surprises—just honest, upfront pricing.",
            highlight: "100% transparency"
          },
          {
            icon: (
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ),
            title: "Real Warranties", 
            description: "Brand‑backed warranties stored digitally in your app with instant access to service and support.",
            highlight: "Digital warranty tracking"
          },
          {
            icon: (
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            ),
            title: "Single Window Support", 
            description: "One unified team handles everything from design to after-sales service. No coordination headaches.",
            highlight: "Unified experience"
          },
          {
            icon: (
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            ),
            title: "Scales Like Software", 
            description: "Modular factory bays allow us to rapidly launch new divisions while maintaining the same quality standards.",
            highlight: "Infinite scalability"
          },
        ].map((benefit, idx) => (
          <div key={idx} className="group relative bg-slate-50 rounded-2xl p-8 hover:bg-white hover:shadow-lg transition-all duration-300 border border-transparent hover:border-slate-200">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-xl bg-superhomes-100 text-superhomes-600 flex items-center justify-center group-hover:bg-superhomes-500 group-hover:text-white transition-colors">
                  {benefit.icon}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-3">{benefit.description}</p>
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-superhomes-50 text-superhomes-700 text-xs font-medium border border-superhomes-100">
                  {benefit.highlight}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Factory = () => (
  <section id="factory" className="py-16 sm:py-24 bg-slate-50">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Modular Giga‑Factory</h2>
        <p className="mt-4 text-lg text-slate-600 max-w-4xl mx-auto">
          Our revolutionary Lego‑style factory with shared utilities enables rapid scaling. New divisions plug into the same backbone infrastructure for consistent quality and efficiency.
        </p>
      </div>

      {/* Isometric Factory View */}
      <div className="relative bg-gradient-to-br from-slate-100 via-white to-slate-50 rounded-3xl p-8 shadow-lg border border-slate-200 overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        {/* Factory Layout */}
        <div className="relative">
          {/* Shared Infrastructure Header */}
          <div className="mb-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-superhomes-100 text-superhomes-700 rounded-full text-sm font-medium">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Shared Infrastructure: Power • Compressed Air • Conveyors • QA Lab
            </div>
          </div>

          {/* Conceptual Isometric Factory View */}
          <div className="relative mx-auto max-w-5xl mb-8">
            {/* Factory Building - Isometric View */}
            <div className="relative">
              {/* Main Factory Structure */}
              <div className="relative bg-white rounded-3xl border-2 border-slate-300 shadow-2xl overflow-hidden" style={{ perspective: '1200px' }}>
                
                {/* Factory Roof - Isometric top */}
                <div className="h-8 bg-gradient-to-br from-slate-400 to-slate-500 relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                  <div className="absolute right-0 top-0 w-8 h-full bg-gradient-to-l from-slate-600 to-slate-500 transform skew-x-12 origin-top-right"></div>
                </div>

                {/* Factory Interior Layout */}
                <div className="relative p-8 bg-gradient-to-br from-slate-50 to-slate-100" style={{ minHeight: '400px' }}>
                  
                  {/* Shared Infrastructure Spine */}
                  <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-full max-w-md">
                    <div className="h-6 bg-gradient-to-r from-superhomes-400 to-superhomes-500 rounded-full shadow-lg flex items-center justify-center">
                      <div className="text-xs font-semibold text-white">SHARED BACKBONE</div>
                    </div>
                    {/* Utilities flowing down */}
                    <div className="w-2 h-24 bg-gradient-to-b from-superhomes-400 to-superhomes-300 mx-auto rounded-b-full opacity-60"></div>
                  </div>

                  {/* Production Bays Layout */}
                  <div className="grid grid-cols-4 gap-6 mt-16">
                    {/* Live Bays */}
                    <div className="relative">
                      <div className="h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl border-2 border-blue-300 shadow-lg">
                        <div className="p-3">
                          <div className="text-xs font-bold text-blue-800">Marvel Homes</div>
                          <div className="text-xs text-blue-600 mt-1">Interior Bay</div>
                          <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2"></div>
                        </div>
                      </div>
                      {/* Isometric side */}
                      <div className="absolute -right-1 top-1 w-4 h-full bg-gradient-to-b from-blue-200 to-blue-300 transform skew-y-12 origin-top-right rounded-r-2xl"></div>
                    </div>

                    <div className="relative">
                      <div className="h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl border-2 border-gray-300 shadow-lg">
                        <div className="p-3">
                          <div className="text-xs font-bold text-gray-800">Metal Store</div>
                          <div className="text-xs text-gray-600 mt-1">Fabrication</div>
                          <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2"></div>
                        </div>
                      </div>
                      <div className="absolute -right-1 top-1 w-4 h-full bg-gradient-to-b from-gray-200 to-gray-300 transform skew-y-12 origin-top-right rounded-r-2xl"></div>
                    </div>

                    <div className="relative">
                      <div className="h-20 bg-gradient-to-br from-teal-100 to-teal-200 rounded-2xl border-2 border-teal-300 shadow-lg">
                        <div className="p-3">
                          <div className="text-xs font-bold text-teal-800">Just Bathrooms</div>
                          <div className="text-xs text-teal-600 mt-1">Renovation</div>
                          <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2"></div>
                        </div>
                      </div>
                      <div className="absolute -right-1 top-1 w-4 h-full bg-gradient-to-b from-teal-200 to-teal-300 transform skew-y-12 origin-top-right rounded-r-2xl"></div>
                    </div>

                    <div className="relative">
                      <div className="h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-2xl border-2 border-green-300 shadow-lg">
                        <div className="p-3">
                          <div className="text-xs font-bold text-green-800">Vantage</div>
                          <div className="text-xs text-green-600 mt-1">Windows</div>
                          <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2"></div>
                        </div>
                      </div>
                      <div className="absolute -right-1 top-1 w-4 h-full bg-gradient-to-b from-green-200 to-green-300 transform skew-y-12 origin-top-right rounded-r-2xl"></div>
                    </div>
                  </div>

                  {/* Future Expansion Bays */}
                  <div className="grid grid-cols-4 gap-6 mt-6">
                    <div className="relative">
                      <div className="h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl border-2 border-dashed border-slate-300 shadow-sm">
                        <div className="p-3 text-center">
                          <div className="text-xs font-semibold text-slate-600">Future Bay</div>
                          <div className="text-xs text-slate-500 mt-1">Ready to Plug</div>
                        </div>
                      </div>
                      <div className="absolute -right-1 top-1 w-4 h-full bg-gradient-to-b from-slate-200 to-slate-300 transform skew-y-12 origin-top-right rounded-r-2xl opacity-60"></div>
                    </div>

                    <div className="relative">
                      <div className="h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl border-2 border-dashed border-slate-300 shadow-sm">
                        <div className="p-3 text-center">
                          <div className="text-xs font-semibold text-slate-600">Future Bay</div>
                          <div className="text-xs text-slate-500 mt-1">Ready to Plug</div>
                        </div>
                      </div>
                      <div className="absolute -right-1 top-1 w-4 h-full bg-gradient-to-b from-slate-200 to-slate-300 transform skew-y-12 origin-top-right rounded-r-2xl opacity-60"></div>
                    </div>

                    <div className="relative">
                      <div className="h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl border-2 border-dashed border-slate-300 shadow-sm">
                        <div className="p-3 text-center">
                          <div className="text-xs font-semibold text-slate-600">Future Bay</div>
                          <div className="text-xs text-slate-500 mt-1">Ready to Plug</div>
                        </div>
                      </div>
                      <div className="absolute -right-1 top-1 w-4 h-full bg-gradient-to-b from-slate-200 to-slate-300 transform skew-y-12 origin-top-right rounded-r-2xl opacity-60"></div>
                    </div>

                    <div className="relative">
                      <div className="h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl border-2 border-dashed border-slate-300 shadow-sm">
                        <div className="p-3 text-center">
                          <div className="text-xs font-semibold text-slate-600">Future Bay</div>
                          <div className="text-xs text-slate-500 mt-1">Ready to Plug</div>
                        </div>
                      </div>
                      <div className="absolute -right-1 top-1 w-4 h-full bg-gradient-to-b from-slate-200 to-slate-300 transform skew-y-12 origin-top-right rounded-r-2xl opacity-60"></div>
                    </div>
                  </div>

                  {/* Conveyor System Visualization */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="h-2 bg-gradient-to-r from-slate-400 via-superhomes-400 to-slate-400 rounded-full opacity-60"></div>
                    <div className="text-center text-xs text-slate-600 mt-2">Overhead Conveyor System</div>
                  </div>
                </div>

                {/* Factory Side Wall */}
                <div className="absolute top-8 -right-2 w-6 bg-gradient-to-b from-slate-300 to-slate-400 shadow-xl transform skew-y-12 origin-top-right" style={{ height: 'calc(100% - 2rem)' }}></div>
              </div>

              {/* Factory Base/Foundation */}
              <div className="absolute -bottom-2 left-2 right-0 h-4 bg-gradient-to-r from-slate-500 to-slate-600 rounded-b-3xl transform skew-y-1 origin-bottom-left shadow-2xl"></div>
            </div>
          </div>

          {/* Shared Services */}
          <div className="flex flex-wrap justify-center gap-4 text-xs text-slate-500">
            {[
              "🔌 Unified Power Grid",
              "💨 Compressed Air Network", 
              "🔄 Overhead Conveyor System",
              "🧪 Centralized Quality Lab",
              "📊 Integrated ERP System",
              "🚛 Shared Loading Docks"
            ].map((service, i) => (
              <span key={i} className="px-3 py-1 bg-white rounded-full border border-slate-200 shadow-sm">
                {service}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Expansion Philosophy */}
      <div className="mt-12 text-center">
        <div className="inline-flex items-center gap-2 px-6 py-3 bg-superhomes-500 text-white rounded-xl font-semibold text-sm">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
          </svg>
          New divisions plug in like Lego blocks — same quality, new capabilities
        </div>
      </div>
    </div>
  </section>
);

const Roadmap = ({ onOpenSubscribe }) => (
  <section id="roadmap" className="py-16 sm:py-24 bg-slate-50">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="flex items-end justify-between gap-6">
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Coming Soon</h2>
          <p className="mt-3 text-slate-600 max-w-3xl">We launch new divisions like Lego blocks—plugging into the same backbone. Be first to know when we go live in your city.</p>
        </div>
        <div className="hidden sm:block">
          <button onClick={onOpenSubscribe} className="inline-flex items-center rounded-xl bg-superhomes-500 px-4 py-2 text-sm font-semibold text-white hover:bg-superhomes-600">Subscribe for Updates</button>
        </div>
      </div>
      <div className="mt-10 grid gap-6 sm:grid-cols-3">
        {[
          {t: "Modular Kitchens & Wardrobes", when: "Q4 '25"},
          {t: "Lighting & Home Automation", when: "Q1 '26"},
          {t: "Flooring & Tiles", when: "Q2 '26"},
        ].map((r, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="text-sm text-slate-500">{r.when}</div>
            <h3 className="mt-1 text-lg font-semibold text-slate-900">{r.t}</h3>
            <button onClick={onOpenSubscribe} className="mt-4 inline-flex items-center rounded-lg border border-superhomes-200 px-3 py-1.5 text-sm font-semibold text-slate-900 hover:bg-superhomes-50">Notify me</button>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="border-t border-slate-200 bg-white">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <img src={LOGO_SRC} alt="Super Homes" className="h-12 w-auto" />
          <p className="mt-3 text-sm text-slate-600 max-w-xs">Design to installation, factory‑fast. Super app + super store for everything interiors.</p>
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-900">Divisions</div>
          <ul className="mt-3 space-y-2 text-sm text-slate-600">
            <li><a href="https://marvelhomes.pro" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900">Marvel Homes</a></li>
            <li><a href="https://themetal.store" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900">The Metal Store</a></li>
            <li><a href="https://justbathrooms.in/" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900">Just Bathrooms</a></li>
            <li><a href="https://vantagewindows.in/" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900">Vantage</a></li>
          </ul>
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-900">Company</div>
          <ul className="mt-3 space-y-2 text-sm text-slate-600">
            <li><a href="#vision" className="hover:text-slate-900">Vision</a></li>
            <li><a href="#factory" className="hover:text-slate-900">Factory</a></li>
            <li><a href="#roadmap" className="hover:text-slate-900">Roadmap</a></li>
            <li><a href="https://investors.superhomes.app" className="hover:text-slate-900">Investors</a></li>
          </ul>
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-900">Legal</div>
          <ul className="mt-3 space-y-2 text-sm text-slate-600">
            <li><a href="#" className="hover:text-slate-900">Privacy</a></li>
            <li><a href="#" className="hover:text-slate-900">Terms</a></li>
          </ul>
        </div>
      </div>
      <div className="mt-10 flex items-center justify-between border-t border-slate-200 pt-6 text-xs text-slate-500">
        <p>© {new Date().getFullYear()} Super Homes. All rights reserved.</p>
        <p>Proudly Made in India for the World</p>
      </div>
    </div>
  </footer>
);

// Subscribe Modal
const SubscribeModal = ({ open, onClose }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    whatsapp: "",
    city: "",
    interests: [],
    consent: false,
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");

  const toggleInterest = (k) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(k)
        ? f.interests.filter((x) => x !== k)
        : [...f.interests, k],
    }));
  };

  const validEmail = (e) => /.+@.+\..+/.test(e);
  const validPhone = (p) => !p || /^\+?[0-9]{10,15}$/.test(p.replace(/\s|-/g, ""));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email) return setError("Email is required.");
    if (!validEmail(form.email)) return setError("Please enter a valid email.");
    if (!validPhone(form.whatsapp)) return setError("Enter WhatsApp in E.164 (e.g., +9198…) or leave blank.");
    if (!form.consent) return setError("Please provide consent to receive updates.");

    try {
      setStatus("submitting");
      // POST to backend — replace with real call. Keeping a graceful fallback demo.
      const res = await fetch(SUBSCRIBE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email,
          whatsapp: form.whatsapp || undefined,
          name: form.name || undefined,
          city: form.city || undefined,
          interests: form.interests,
          consent: form.consent,
        }),
      });

      if (!res.ok) throw new Error("Subscription failed");
      setStatus("success");
    } catch (err) {
      // Fallback success for demo if endpoint not available
      setStatus("success");
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
          {status !== "success" ? (
            <>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">Subscribe for Super Homes updates</h3>
                  <p className="mt-1 text-sm text-slate-600">Occasional milestones only. No spam.</p>
                </div>
                <button aria-label="Close" onClick={onClose} className="rounded-lg p-2 hover:bg-slate-100">
                  <IconClose className="h-6 w-6 text-slate-700" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="mt-4 grid gap-3">
                <div className="grid gap-2">
                  <label className="text-sm font-medium text-slate-700" htmlFor="name">Name (optional)</label>
                  <input id="name" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-superhomes-400" placeholder="Jane" />
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium text-slate-700" htmlFor="email">Email</label>
                  <input id="email" required value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-superhomes-400" placeholder="jane@example.com" />
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium text-slate-700" htmlFor="wa">WhatsApp (optional)</label>
                  <input id="wa" value={form.whatsapp} onChange={(e)=>setForm({...form, whatsapp: e.target.value})} className="w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-superhomes-400" placeholder="+9198XXXXXXXX" />
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium text-slate-700" htmlFor="city">City (optional)</label>
                  <input id="city" value={form.city} onChange={(e)=>setForm({...form, city: e.target.value})} className="w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-superhomes-400" placeholder="Vadodara" />
                </div>

                <fieldset className="mt-2">
                  <legend className="text-sm font-medium text-slate-700">Interests</legend>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {[
                      "Interiors",
                      "Windows",
                      "Fabrication",
                      "Bathrooms",
                      "All future launches",
                    ].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => toggleInterest(opt)}
                        className={classNames(
                          "rounded-full px-3 py-1 text-sm border",
                          form.interests.includes(opt)
                            ? "bg-superhomes-500 text-white border-superhomes-500"
                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                        )}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <label className="mt-2 flex items-start gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={form.consent} onChange={(e)=>setForm({...form, consent: e.target.checked})} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400" />
                  <span>I agree to receive updates from Super Homes via the selected channels. I can unsubscribe anytime.</span>
                </label>

                {error && <div role="alert" className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">{error}</div>}

                <div className="mt-1 flex items-center justify-end gap-2">
                  <button type="button" onClick={onClose} className="inline-flex items-center rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50">Cancel</button>
                  <button type="submit" disabled={status === "submitting"} className="inline-flex items-center rounded-xl bg-superhomes-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 hover:bg-superhomes-600">
                    {status === "submitting" ? "Submitting…" : "Subscribe"}
                  </button>
                </div>

                <p className="mt-2 text-xs text-slate-500">We send at most two updates per month. Read our <a href="#" className="underline hover:text-slate-700">Privacy Policy</a>.</p>
              </form>
            </>
          ) : (
            <div className="text-center p-6">
              <div className="mx-auto h-14 w-14 rounded-2xl bg-superhomes-500 text-white grid place-items-center"><IconCheck className="h-7 w-7"/></div>
              <h3 className="mt-4 text-xl font-semibold text-slate-900">You're on the list—welcome to Super Homes.</h3>
              <p className="mt-2 text-sm text-slate-600">We'll send occasional milestone updates only. Watch this space!</p>
              <button onClick={onClose} className="mt-6 inline-flex items-center rounded-xl bg-superhomes-500 px-4 py-2 text-sm font-semibold text-white hover:bg-superhomes-600">Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function SuperHomesLanding() {
  const [subscribeOpen, setSubscribeOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header onOpenSubscribe={() => setSubscribeOpen(true)} />
      <main>
        <Hero onOpenSubscribe={() => setSubscribeOpen(true)} />
        <Vision />
        <Divisions />
        <WhyUs />
        <Factory />
        <Roadmap onOpenSubscribe={() => setSubscribeOpen(true)} />
        <section id="subscribe" className="py-16 bg-gradient-to-r from-slate-900 to-superhomes-900 text-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-6 sm:grid-cols-2 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold">Be first to know.</h2>
              <p className="mt-2 text-slate-300 max-w-prose">Get launch updates for new divisions, factory milestones, and city rollouts. No spam.</p>
            </div>
            <div className="justify-self-start sm:justify-self-end">
              <button onClick={()=>setSubscribeOpen(true)} className="inline-flex items-center rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-slate-900 shadow hover:bg-slate-50">Subscribe for Updates</button>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <SubscribeModal open={subscribeOpen} onClose={() => setSubscribeOpen(false)} />
    </div>
  );
}